import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { isMobile } from "react-device-detect";
import { useUserAuth } from "../../context/UserAuthContext";
import React from "react";
import styled from "styled-components";
import theme from "../../styles/styleTheme";
import { useNavigate } from "react-router-dom";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import NAV from "../../utils/nav.const";
import { usePayment } from "../../hooks/payment/usePayment";
import { useTranslation } from "react-i18next";
import { validatePasswordRequirements } from "../../utils/validatePasswordRequirements";

type AccountFormProps = {
  handleBackToOverview: () => void;
  isPremium: boolean;
};

const AccountForm = ({ handleBackToOverview, isPremium }: AccountFormProps) => {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createPayment } = usePayment();
  const { createTenant } = useUserAuth();
  const [termsAccepted, setTermsAccepted] = useState<any>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>({
    message: "",
    active: false,
  });
  const [showPassword, setShowPassword] = useState<any>({
    password: false,
    confirmPassword: false,
  });
  const [user, setUser] = useState<any>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleTermsAndConditions = (e: any) => {
    const { checked } = e.target;
    setTermsAccepted(checked);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser((prevUser: any) => ({ ...prevUser, [name]: value }));
    setError({ message: "", active: false });
  };

  const createAccount = async () => {
    if (
      !user.firstName ||
      !user.lastName ||
      !user.phoneNumber ||
      !user.email ||
      !user.password ||
      !user.confirmPassword
    ) {
      setError({
        message: t("accountform.error.fields"),
        active: true,
      });
      return;
    }
    if (!validatePasswordRequirements(user.password)) {
      setError({
        message: t("accountform.error.invalidPassword"),
        active: true,
      });
      return;
    }

    if (user.password !== user.confirmPassword) {
      setError({
        message: t("accountform.error.passwordsDontMatch"),
        active: true,
      });
      return;
    }

    if (!termsAccepted) {
      setError({
        message: t("accountform.error.terms"),
        active: true,
      });
      return;
    }

    setLoading(true);
    setError({ message: "", active: false });

    await createTenant(
      user.firstName,
      user.lastName,
      user.phoneNumber,
      user.email,
      user.password
    )
      .then((result: any) => {
        setLoading(false);
        
        if (isPremium) {
          createPayment(result.id);
        }
        if (!isPremium) {
          navigate("/" + NAV.ACCOUNT_CONFIRMATION);
        }
      })
      .catch((err: any) => {
        setError({ message: err.message, active: true });
        setLoading(false);
      });
  };
  return (
    <Grid
      sx={{
        backgroundColor: "#fff",
        padding: isSmallScreen ? "1.2rem" : "3rem",
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
        borderRadius: 4,
        width: isSmallScreen ? "100%" : 1000,
      }}
      container
      display={"flex"}
      gap={2}
    >
      <Grid item xs={12}>
        <Button
          onClick={handleBackToOverview}
          startIcon={<KeyboardArrowLeftIcon />}
        >
          {t("profile.back")}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h3">
          {isMobile
            ? `${t("profile.your_details")}- ${
                isPremium ? "Premium" : "Basis"
              } account`
            : t("profile.your_details")}
        </Typography>
      </Grid>
      <Grid height={50} item xs={12}>
        <Typography
          sx={{
            color: "#FF7C3A",
          }}
          variant="body2"
        >
          {error?.message}
        </Typography>
      </Grid>

      <Grid
        display={"flex"}
        flexDirection={isSmallScreen ? "column" : "row"}
        gap={2}
        xs={12}
      >
        <TextField
          color="primary"
          fullWidth
          label={t("createAccount.firstname")}
          variant="outlined"
          name="firstName"
          value={user.firstName}
          onChange={handleChange}
          placeholder="John"
        />
        <TextField
          color="primary"
          fullWidth
          label={t("createAccount.lastName")}
          variant="outlined"
          name="lastName"
          value={user.lastName}
          onChange={handleChange}
          placeholder="de Vries"
        />
      </Grid>

      <Grid
        display={"flex"}
        flexDirection={isSmallScreen ? "column" : "row"}
        gap={2}
        xs={12}
      >
        <TextField
          color="primary"
          fullWidth
          label={t("createAccount.email")}
          variant="outlined"
          name="email"
          value={user.email}
          onChange={handleChange}
          placeholder="hello@example.nl"
        />
        <TextField
          type={"tel"}
          color="primary"
          fullWidth
          label={t("createAccount.phone")}
          variant="outlined"
          name="phoneNumber"
          value={user.phoneNumber}
          onChange={handleChange}
          placeholder="+3053434343"
        />
      </Grid>

      <Grid
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        flexWrap={isSmallScreen ? "wrap" : "nowrap"}
        container
        item
        xs={12}
        gap={2}
      >
        <FormControl fullWidth variant="outlined">
          <InputLabel>{t("createAccount.password")}</InputLabel>
          <OutlinedInput
            fullWidth
            type={showPassword.password ? "text" : "password"}
            name="password"
            value={user.password}
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={() =>
                    setShowPassword((prev: any) => ({
                      ...prev,
                      password: !prev.password,
                    }))
                  }
                >
                  {showPassword.password ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            }
            label={t("createAccount.password")}
          />
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel>{t("createAccount.confirm_password")}</InputLabel>
          <OutlinedInput
            fullWidth
            type={showPassword.confirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={user.confirmPassword}
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={() =>
                    setShowPassword((prev: any) => ({
                      ...prev,
                      confirmPassword: !prev.confirmPassword,
                    }))
                  }
                >
                  {showPassword.confirmPassword ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            }
            label={t("createAccount.confirm_password")}
          />
        </FormControl>
      </Grid>

      <Grid
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        flexWrap={"nowrap"}
        container
        item
        xs={12}
        gap={2}
      >
        <Grid xs={12}>
          <Box
            marginTop={2}
            marginBottom={4}
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
          >
            <Checkbox onChange={handleTermsAndConditions} />
            <Typography variant="body2">
              {t("createAccount.terms1")}{" "}
              <Link
              href={
                process.env.REACT_APP_WORDPRESS_URL + "/algemene-voorwaarden/"
              }
              target="_blank"
              >
                <Box
                  sx={{
                    display: "inline",
                    color: theme.palette.primary.main,
                    borderBottom: "1px solid " + theme.palette.primary.main,
                  }}
                >
                  {t("createAccount.terms2")}
                </Box>
              </Link>
            </Typography>
          </Box>
          <Button fullWidth variant="contained" onClick={createAccount}>
            {loading ? (
              <CircularProgress
                size={25}
                sx={{
                  color: "#fff",
                }}
              />
            ) : (
              t("createAccount.createAccount")
            )}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AccountForm;

export const Link = styled.a<{
  href: string;
  children: React.ReactNode;
  target: string;
}>`
  text-decoration: none;
  font-family: "Quicksand", sans-serif;
  color: #000;

  &:hover {
    text-decoration: underline;
    color: #5b41ff;
  }
`;
